import { useCallback } from "react";
import { useQueryClient } from "@tanstack/react-query";
import {
  RouteGetMerchflowStorePog_storePogId_DeliveryConflicts,
  type DeliveryGoLiveConflict,
  type ResponseGetMerchflowStorePog_storePogId_DeliveryConflicts,
} from "@CommonApi/merchflow/store/pog/_storePogId_/delivery-conflicts";
import {
  RouteGetMerchflows_MerchflowId_StoresPogsDeliveryConflicts,
  type ResponseGetMerchflows_MerchflowId_StoresPogsDeliveryConflicts,
} from "@CommonApi/merchflows/_merchflowId_/stores/pogs/delivery-conflicts";
import { useModals } from "src/components/Modals";
import { useToasts } from "src/components/Toasts";
import { Color, Flex, Text } from "src/elements";
import { useTranslation } from "src/i18n";
import { API, getRequest } from "src/utils/axios";

/** What a "Mark as delivered" action delivers: one store POG, or every store of a merchflow. */
export type DeliveryTarget = { storePogId: number } | { merchflowId: number };

const MAX_LISTED_CONFLICTS = 5;

export const deliveryConflictsQueryKey = (target: DeliveryTarget) =>
  "storePogId" in target
    ? ["delivery-conflicts", "store-pog", target.storePogId]
    : ["delivery-conflicts", "merchflow", target.merchflowId];

const fetchDeliveryConflicts = async (target: DeliveryTarget, signal: AbortSignal) => {
  if ("storePogId" in target) {
    const { data } = await getRequest<ResponseGetMerchflowStorePog_storePogId_DeliveryConflicts>(
      `${API}${RouteGetMerchflowStorePog_storePogId_DeliveryConflicts}`,
      { params: { storePogId: target.storePogId }, signal },
    );
    return data.conflicts;
  }
  const { data } = await getRequest<ResponseGetMerchflows_MerchflowId_StoresPogsDeliveryConflicts>(
    `${API}${RouteGetMerchflows_MerchflowId_StoresPogsDeliveryConflicts}`,
    { params: { merchflowId: target.merchflowId }, signal },
  );
  return data.conflicts;
};

/**
 * Wraps a "Mark as delivered" action: when it would leave a store with two delivered planograms
 * for the same go-live date, the user is warned and chooses whether to deliver anyway.
 */
export const useConfirmDeliveryConflicts = () => {
  const { t } = useTranslation("planogramDelivery");
  const queryClient = useQueryClient();
  const { openConfirmModal } = useModals();
  const { toast } = useToasts();

  return useCallback(
    async (target: DeliveryTarget, deliver: () => Promise<void>) => {
      let conflicts: DeliveryGoLiveConflict[];
      try {
        // Always asked fresh: another delivery may have landed since the last check.
        conflicts = await queryClient.fetchQuery({
          queryKey: deliveryConflictsQueryKey(target),
          queryFn: ({ signal }) => fetchDeliveryConflicts(target, signal),
          staleTime: 0,
        });
      } catch (error) {
        toast({ title: t("markDelivered.conflictCheckFailed"), error, type: "ERROR" });
        return;
      }

      if (conflicts.length === 0) {
        await deliver();
        return;
      }

      openConfirmModal({
        title: t("markDelivered.conflictTitle"),
        icon: { name: "alert", color: Color.yellow },
        width: "560px",
        description: (
          <Flex column gap="8px">
            <Text variant="small2" color={Color.textSecondary}>
              {t("markDelivered.conflictDescription")}
            </Text>
            {conflicts.slice(0, MAX_LISTED_CONFLICTS).map((conflict) => (
              <Text
                key={`${conflict.store_pog_id}-${conflict.delivered_store_pog_id}`}
                variant="small2"
              >
                {t("markDelivered.conflictRow", {
                  store: conflict.store,
                  category: conflict.category_code,
                  date: conflict.go_live_date,
                  storePogId: conflict.delivered_store_pog_id,
                  // The later delivery is the one the store runs: the one being delivered now.
                  merchflow: conflict.merchflow_name,
                  deliveredMerchflow:
                    conflict.delivered_merchflow_name ??
                    t("markDelivered.conflictUnknownMerchflow"),
                })}
              </Text>
            ))}
            {conflicts.length > MAX_LISTED_CONFLICTS && (
              <Text variant="small2" color={Color.textSecondary}>
                {t("markDelivered.conflictMore", {
                  count: conflicts.length - MAX_LISTED_CONFLICTS,
                })}
              </Text>
            )}
          </Flex>
        ),
        buttons: [
          { id: "cancel", label: t("markDelivered.conflictCancel"), variant: "borderOnly" },
          {
            id: "deliver",
            label: t("markDelivered.conflictDeliverAnyway"),
            color: "yellow",
            onClick: deliver,
          },
        ],
      });
    },
    [openConfirmModal, queryClient, t, toast],
  );
};
